// Spec 078 US5 / FR-039 — gerar e gerir links de exibição da rota ambiente. O link completo só é
// mostrado UMA vez (o backend guarda o hash); copiar é a ação primária logo após gerar.
import * as React from "react";
import { Copy, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ModalOverlay } from "@/components/ui/modal-overlay";
import { Notify, toast } from "@/components/ui/notify";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { getFriendlyErrorMessage } from "@/lib/api-client";
import { DisplayLinksList } from "./DisplayLinksList";
import { useCreateDisplayToken, useDisplayTokens } from "./hooks";

export interface DisplayLinksDialogProps {
  lineId: string;
  lineName: string;
  onClose: () => void;
}

export function DisplayLinksDialog({
  lineId,
  lineName,
  onClose,
}: DisplayLinksDialogProps): React.ReactElement {
  const list = useDisplayTokens(lineId);
  const create = useCreateDisplayToken(lineId);
  const copy = useCopyToClipboard();
  const [label, setLabel] = React.useState("");
  const [fresh, setFresh] = React.useState<string | null>(null);

  async function generate() {
    try {
      const res = await create.mutateAsync(label.trim() ? { label: label.trim() } : {});
      setFresh(`${window.location.origin}${res.url}`);
      setLabel("");
    } catch (err) {
      toast.error("Não foi possível gerar o link.", { description: getFriendlyErrorMessage(err) });
    }
  }

  return (
    <ModalOverlay onClose={onClose} title={`Links de exibição de ${lineName}`} className="max-w-xl">
      <div className="flex flex-col gap-4 p-5" data-testid="display-links-dialog">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">Links de exibição</h2>
          <p className="text-sm text-text-secondary">
            Um link abre a cena de “{lineName}” numa TV sem login. Quem tem o link vê o estado das
            máquinas; revogue quando a TV for desligada ou o link vazar.
          </p>
        </div>

        {fresh ? (
          <Notify.Banner
            variant="success"
            title="Link gerado — copie agora"
            description="Por segurança ele não será mostrado de novo. Cole na TV (ou no navegador dela) e pronto."
          >
            <div className="mt-2 flex items-center gap-2">
              <Input
                readOnly
                value={fresh}
                aria-label="Link de exibição"
                className="font-mono text-xs"
              />
              <Button
                size="sm"
                onClick={() => copy(fresh, "Link copiado.")}
                aria-label="Copiar link"
              >
                <Copy className="h-4 w-4" aria-hidden />
                Copiar
              </Button>
            </div>
          </Notify.Banner>
        ) : null}

        <form
          className="flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void generate();
          }}
        >
          <div className="flex-1 space-y-1.5">
            <Label htmlFor="display-link-label">Rótulo (onde fica a TV)</Label>
            <Input
              id="display-link-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="TV do corredor B"
              maxLength={60}
              autoComplete="off"
            />
          </div>
          <Button type="submit" disabled={create.isPending}>
            <Link2 className="h-4 w-4" aria-hidden />
            Gerar link
          </Button>
        </form>

        {list.isError ? (
          <Notify.QueryError
            error={list.error}
            variant="inline"
            onRetry={() => void list.refetch()}
          />
        ) : list.data ? (
          <DisplayLinksList lineId={lineId} items={list.data} />
        ) : (
          <p className="text-sm text-text-tertiary">Carregando…</p>
        )}

        <div className="flex justify-end border-t pt-3">
          <Button variant="ghost" onClick={onClose}>
            Fechar
          </Button>
        </div>
      </div>
    </ModalOverlay>
  );
}
